<template>
  <v-app>
    <div v-if="isLogin || consent">
      <login v-if="isLogin" />
      <consent
        v-else-if="consent"
      />
    </div>
    <no-access
      v-else-if="noAccess"
    />
    <div
      v-else
      class="app-container"
    >
      <ngc-banner
        v-if="showBanner"
        variant="warning"
        class="evault-banner"
        data-cy="app-banner"
      >
        <!-- Message and buttons all go in the default slot, deliberately
             leaving the actions slot empty. ngc-banner derives `hasActions`
             from that slot's assignedElements and swaps
             .gator-banner_centered (justify-content: center) for
             .gator-banner_left-aligned (space-between, content-wrapper
             justify-content: start, actions margin-left: auto) the moment
             anything is assigned to it - which is what pushed the buttons to
             the far right. sn-banner was a single centred row (icon, message,
             then text buttons), so parity needs that slot left alone.

             The default slot lands inside .gator-banner-content, which is
             flex-direction: column, so a stack of siblings would break onto
             separate lines. Wrapping them in one element makes it a single
             flex item, and the wrapper is light DOM so it can be laid out
             from this component's styles. -->
        <div class="evault-banner-content">
          <span>{{ bannerText }}</span>
          <ngc-button
            v-if="maySwitchToAdmin"
            variant="base"
            start-icon="exit-left"
            class="mr-2"
            @click="becomeSnAdmin"
          >
            Switch to Admin View Only
          </ngc-button>
          <ngc-button
            v-if="maySwitchToProduction"
            variant="base"
            start-icon="tab-arrows"
            @click="toggleIsStaging"
          >
            Switch to Production
          </ngc-button>
        </div>
      </ngc-banner>
      <div class="flex evault-container">
        <nav
          ref="sideMenu"
          :key="sideMenuKey"
          class="side-menu"
          :style="{ width: `${navWidth}px` }"
          aria-label="eVault navigation"
        >
          <router-link
            :to="homeLink"
            class="side-menu-logo"
          >
            <img
              :src="ncinoLogo"
              alt="nCino eVault"
            >
          </router-link>
          <ngc-list
            :key="navListKey"
            ref="sideMenuList"
            class="side-menu-list"
            selectable
            hide-checkmarks
          >
            <template v-for="(item, index) in sideNavOptions">
              <ngc-divider
                v-if="item.type === 'divider'"
                :key="`divider-${index}`"
                class="side-menu-divider"
              />
              <ngc-list-item
                v-else
                :key="item.link"
                :text="item.title"
                :value.prop="item.link"
                density="comfortable"
                :selected.prop="isCurrentNavItem(item.link)"
                hide-checkmark
                :tabindex.prop="0"
                :aria-current="isCurrentNavItem(item.link) ? 'page' : 'false'"
                :data-cy="navItemDataCy(item.title)"
                @selected="navigateTo(item.link)"
              >
                <ngc-icon
                  slot="start"
                  :name="item.icon"
                />
              </ngc-list-item>
            </template>
          </ngc-list>
          <viewing-as
            v-if="showViewingAs"
            ref="viewingAs"
            class="side-menu-footer"
            :user-name="userFullName"
          />
        </nav>
        <div class="content evault-content">
          <ngc-page-header
            :key="headerValue"
            class="evault-header"
            :title.prop="headerText"
            :subtitle="subtitleText"
            :skeleton="$store.getters.showHeaderLoaderSkeleton"
            data-cy="evault-header"
          >
            <ngc-breadcrumbs
              v-if="breadcrumbs && breadcrumbs.length"
              slot="breadcrumbs"
            >
              <!-- current comes from the store's `disabled` flag, which
                   setBreadcrumbs stamps on the last crumb only and which is
                   what sn-header consumed. Deriving it from a missing href
                   instead marks every href-less crumb current - companyDetails
                   passes { Companies, href }, { company name },
                   { Organizations } and transactionGroupDetails the same shape,
                   so two crumbs rendered aria-current="page" and the middle one
                   lost its link styling. -->
              <ngc-breadcrumb
                v-for="(item, index) in breadcrumbs"
                :key="`${item.text}-${index}`"
                :label="item.text"
                :href="item.href || undefined"
                :current="item.disabled"
                :data-cy="`${item.text}-bc`"
              />
            </ngc-breadcrumbs>
            <ngc-tabset
              v-if="headerTabs.length"
              slot="tabs"
              size="large"
              :active-tab-index.prop="safeActiveTabIndex"
              @select="handleTabSelect"
            >
              <ngc-tab
                v-for="tab in headerTabs"
                :key="tab.id"
                :name="tab.name"
              />
            </ngc-tabset>
            <div
              slot="actions"
              class="header-actions"
            >
              <ngc-avatar
                v-if="userInitials !== ''"
                size="small"
                interactive
                :src="user.profilePictureUrl || ''"
                :avatar-aria-label="userFullName"
                data-cy="profile-icon"
                @click="showProfileDropdown()"
              >
                <!-- ngc-avatar paints slotted content at z-index 1 over the
                     image at z-index 0, so passing both stacks the initials on
                     top of the photo. sn-avatar rendered the image or the
                     initials, never both. -->
                <span v-if="!user.profilePictureUrl">{{ userInitials }}</span>
              </ngc-avatar>
              <profile-dropdown
                v-show="profileDropdownVisible"
                :key="orgGuid"
                :is-shown="profileDropdownVisible"
                :user-name="userFullName"
                :user-guid="user.guid"
                :profile-picture-url="user.profilePictureUrl"
                @close="closeProfileDropdown"
                @logout="logout"
              />
            </div>
          </ngc-page-header>
          <router-view
            v-if="!loading"
            :key="routerKey"
            class="page-content"
          />
        </div>
        <evault-session-timeout-modal
          :display="timeoutModalVisible"
          :total-seconds="timeoutModalTotalSeconds"
          @extend="() => timeoutModalTotalSeconds = undefined"
        />
        <my-profile-nav-dialog
          v-model="profileSettingsVisible"
          :initial-tab="profileSettingsInitialTab"
          :user-guid="user.guid"
        />
        <failed-upload-docs-modal
          :toggle-modal="showFailedUploadDocsModal"
          :title="failedUploadDocsModalTitle"
          :message="failedUploadDocsModalMessage"
          :file-names="failedUploadDocsFileNames"
          @close="toggleFailedUploadDocsModal"
        />
        <error-overflow-modal
          :toggle-modal="showErrorOverflowModal"
          :title="errorOverflowModalTitle"
          :message="errorOverflow"
          @close="toggleErrorOverflowModal"
        />
      </div>
    </div>
    <!--
      ngc-modal (gator) opens via the native <dialog>.showModal(), which promotes
      it to the browser's top layer - nothing positioned with a plain CSS z-index
      (like the legacy sn-toaster below) can render above top-layer content, no
      matter how high the z-index. Wrapping the toaster in its own manual popover
      puts it in the top layer too, so toasts stop rendering behind open modals.
    -->
    <div
      ref="toastPopover"
      popover="manual"
      class="toast-popover"
    >
      <sn-toaster
        v-if="evaultToastMixin_activeToasts.length > 0"
        :toasts="evaultToastMixin_activeToasts"
        @actionClicked="handleToastAction"
      />
    </div>
  </v-app>
</template>

<script>
import ncinoLogo from '~/assets/images/NcinoEvault.svg'
import toastMixin from '../components/mixins/evaultToastMixin'
import ProfileDropdown from './evui/profileDropdown.vue'
import login from './login'
import noAccess from './noAccess.vue'
import consent from './consent.vue'
import { user } from './graphql/users/users_queries.gql'
import { company } from './graphql/companies_queries.gql'
import { orgs, routeOrg } from './graphql/org_queries.gql'
import myProfileNavDialog from './evui/myProfileNavDialog.vue'
import EvaultSessionTimeoutModal from './evui/evaultSessionTimeoutModal'
import ViewingAs from './evui/viewingAs'
import FailedUploadDocsModal from './evui/failedUploadDocsModal'
import ErrorOverflowModal from './evui/errorOverflowModal'
import snEvaultService from '../services/snEvaultService'
import _ from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import cookie from 'js-cookie'
import axios from 'axios'
import snThemeMixin from '~/components/common/snThemeMixin'

export default {
  name: 'EvaultApp',
  components: {
    EvaultSessionTimeoutModal,
    ViewingAs,
    FailedUploadDocsModal,
    ErrorOverflowModal,
    myProfileNavDialog,
    ProfileDropdown,
    login,
    noAccess,
    consent
  },
  mixins: [toastMixin, snThemeMixin],
  data () {
    return {
      headerValue: 0,
      navWidth: 256,
      profileDropdownVisible: false,
      showNotificationDropdown: false,
      showSnoozeModal: false,
      showViewingAs: true,
      isSnAdmin: null,
      activeTabIndex: 0,
      loading: true,
      tabActiveListener: null,
      windowResizeListener: null,
      trueViewingAsHeight: null,
      tabInactiveTimestamp: new Date().getTime(),
      timeoutModalTotalSeconds: undefined,
      showFailedUploadDocsModal: false,
      failedUploadDocsFileNames: [],
      errorOverflow: '',
      errorOverflowModalTitle: '',
      showErrorOverflowModal: false,
      initialTab: 0,
      homeLink: '/',
      ncinoLogo
    }
  },

  computed: {
    ...mapGetters(['user',
      'activeHeaderTab',
      'isStaging',
      'orgs',
      'orgGuid',
      'mersOrgId',
      'timeoutModalVisible',
      'viewAsCompany',
      'profileSettingsVisible',
      'profileSettingsInitialTab'
    ]),

    companyGuid () {
      if (this.user.isSnAdmin) {
        return this.viewAsCompany ? this.viewAsCompany.guid : undefined
      }
      return undefined
    },

    sideMenuKey () {
      // Force side menu to re-render when organization context changes
      return `${this.orgGuid}-${this.mersOrgId}-${this.viewAsCompany?.guid || 'no-company'}-${this.isStaging}`
    },

    bannerText () {
      if (this.isStaging && !this.viewAsCompany) {
        return 'You\'re in the Staging environment.'
      } else if (this.isStaging) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''} in the Staging environment.`
      } else if (this.viewAsCompany) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''}`
      }
      return ''
    },

    showBanner () {
      return this.isStaging || this.viewAsCompany
    },

    routerKey () {
      if (this.orgGuid != null && this.orgGuid !== 'undefined' && this.orgGuid !== '') {
        return this.orgGuid + this.isStaging
      }
      return this.isStaging
    },

    userFullName () {
      if (this.user) {
        return `${this.user.firstName || ''} ${this.user.lastName || ''}`
      }
      return ''
    },

    userInitials () {
      if (this.user) {
        const { firstName, lastName } = this.user
        return `${firstName ? firstName[0] : ''}${lastName ? lastName[0] : ''}`.toUpperCase()
      }
      return ''
    },

    isLogin () {
      return this.$route.path.includes('login')
    },

    noAccess () {
      return this.$route.path.includes('no_access')
    },

    consent () {
      return this.$route.path.includes('consent')
    },

    isEvaultRoot () {
      return this.$route.fullPath.replaceAll('/', '') === 'evault'
    },

    breadcrumbs () {
      if (this.isEvaultRoot) this.setBreadcrumbs([])
      return this.$store.getters.breadcrumbs
    },

    headerTabs () {
      if (this.isEvaultRoot) this.setHeaderTabs([])
      return this.$store.getters.headerTabs
    },

    // ngc-tabset dereferences tabs[activeTabIndex] while rendering, so an index
    // left over from a longer tab list throws and renders no tabs at all -
    // sn-header only mis-highlighted the active tab in that case.
    safeActiveTabIndex () {
      if (this.activeTabIndex < 0 || this.activeTabIndex >= this.headerTabs.length) {
        return 0
      }
      return this.activeTabIndex
    },

    maySwitchToAdmin () {
      return this.viewAsCompany
    },

    maySwitchToProduction () {
      return this.isStaging && snEvaultService.hasPermission(this.user, 'productionEnabled')
    },

    headerText () {
      if (this.$route.fullPath.replaceAll('/', '') === 'evault') {
        this.setHeaderText('Welcome to nCino eVault')
      }
      return this.$store.getters.headerText
    },
    subtitleText () {
      if (this.$route.fullPath.replaceAll('/', '') === 'evault') {
        this.setSubtitleText('Welcome to nCino eVault')
      }
      return this.$store.getters.subtitleText
    },
    sideNavOptions () {
      const enotePermissions = ['transfers', 'deliveries', 'changeStatus', 'changeData', 'register']
      const transactionPermissions = [...enotePermissions, 'inquiry', 'connectivity']
      const settingsPermissions = ['manageOrganizations', 'manageUsers', 'managePartners', 'manageRoles', 'viewAboutTab']
      const hasOrgGuid = this.orgGuid != null && this.orgGuid !== 'undefined' && this.orgGuid !== ''
      const hasMersOrgId = this.mersOrgId != null && this.mersOrgId !== 'undefined' && this.mersOrgId !== ''
      // For admin users, show org nav items if they have org context, regardless of viewAsCompany
      // For non-admin users, show nav items (they don't need viewAsCompany set)
      const hasCompanyOptions = this.isSnAdmin ? (hasOrgGuid && hasMersOrgId) : true

      const options = []

      if (transactionPermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasOrgGuid && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'Transactions',
            icon: 'move-doc',
            link: '/transactions'
          }
        )
      }
      if (enotePermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasOrgGuid && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'eNote Records',
            icon: 'dollar-house',
            link: '/enotes'
          }
        )
      }
      if (snEvaultService.hasPermission(this.user, 'electronicRecordsEnabled') && hasCompanyOptions) {
        options.push({
          title: 'Electronic Records',
          icon: 'folder',
          link: '/electronic_records'
        })
      }
      if (snEvaultService.hasPermission(this.user, 'manageReports') && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'Reports',
            icon: 'document-image',
            link: '/reports'
          }
        )
      }
      if (settingsPermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasCompanyOptions) {
        options.push(
          {
            title: 'Settings',
            icon: 'settings',
            link: '/company_settings'
          }
        )
      }
      if (this.isSnAdmin) {
        if (options.length) {
          options.push({ type: 'divider' })
        }
        options.push(...this.snAdminSideNavOptions)
      }
      const devHosts = ['localhost', 'ncmstaging.com', 'snstaging.com', 'ngrok']
      if (devHosts.some(host => window.location.host.includes(host))) {
        if (!this.isSnAdmin) {
          if (options.length) {
            options.push({ type: 'divider' })
          }
        }

        options.push({
          title: 'MERS Tools',
          icon: 'hammer',
          link: '/mers_tools'
        })
      }
      return options
    },
    snAdminSideNavOptions () {
      const options = [
        {
          title: 'Companies',
          icon: 'city',
          link: '/companies'
        },
        {
          title: 'API Integrations',
          icon: 'link',
          link: '/integrations'
        }
      ]

      if (snEvaultService.hasSupportToolsPermission(this.user)) {
        options.push({
          title: 'Support Tools',
          icon: 'tools',
          link: '/support_tools'
        })
      }
      options.push({
        title: 'Data Dashboard',
        icon: 'case',
        link: '/data_dashboard'
      })
      return options
    },

    // ngc-list attaches a 'selected' listener to each item once and closes over
    // that item's index at attach time. It only re-runs that wiring when its own
    // `selectable`/`multiselect` props change - never when the slotted set does.
    // These options grow after boot as permissions and isSnAdmin resolve, so the
    // captured indices go stale, and the list then moves focus to whichever row
    // now sits at the old index rather than the one clicked. Re-keying forces a
    // remount so the listeners reattach against the current order.
    navListKey () {
      return this.sideNavOptions.map(item => item.link || item.type).join('|')
    },

    failedUploadDocsModalTitle () {
      return `${this.failedUploadDocsFileNames.length} ${this.failedUploadDocsFileNames.length === 1 ? 'document' : 'documents'} did not upload`
    },
    failedUploadDocsModalMessage () {
      return `${this.failedUploadDocsFileNames.length === 1 ? 'This document' : 'These documents'} failed to upload due to connection issues. Please try again.`
    },
    profileSettingsVisible: {
      get () {
        return this.$store.getters.profileSettingsVisible
      },
      set (value) {
        this.setProfileSettingsVisible(value)
      }
    }
  },

  watch: {
    $route () {
      this.resetAppTimeout()
      // Ensure organization context is set up when navigating to routes that require it
      this.$nextTick(async () => {
        if (this.$route.name && this.$route.name !== 'Login') {
          const requiresOrg = snEvaultService.requiresOrg(this.$route.name)
          if (requiresOrg) {
            await this.getRouteOrg()
          }
        }
      })
    },
    headerTabs () {
      this.headerValue++
    },
    activeHeaderTab () {
      this.activeTabIndex = this.headerTabs.map(tab => tab.key).indexOf(this.activeHeaderTab)
    },
    evaultToastMixin_activeToasts () {
      this.syncToastPopover()
    },
    orgGuid () {
      // Force re-evaluation of side navigation when org context changes
      this.$forceUpdate()
    },
    viewAsCompany () {
      // Force re-evaluation of side navigation when company context changes
      this.$forceUpdate()
    },
    // showViewingAs defaults to true and handleWindowResize only ran on a
    // resize event, so on load the footer rendered whether or not it fit - and
    // once a resize hid it, nothing recomputed until the next resize. The nav
    // list arrives asynchronously and changes with org context without any
    // resize following, so the fit has to be recalculated here instead.
    sideNavOptions: {
      immediate: true,
      handler () {
        this.$nextTick(this.handleWindowResize)
      }
    }
  },

  async created () {
    if (this.isLogin) {
      return
    }

    this.loading = true
    const user = await this.getUser()

    if (!user) {
      return
    }
    const { guid, firstName, lastName, locked, profilePictureUrl, timezone, rawPermissions, transactionsRefreshInterval, company } = user
    const permissions = JSON.parse(rawPermissions) || {}
    const companyLocked = company.locked
    const featureFlags = JSON.parse(company.featureFlags) || {}

    /* camelCase and trim permission keys:
        permission_change_status_and_change_data => changeStatusAndChangeData
        access like: this.user.permissions.changeStatusAndChangeData */
    Object.keys(permissions).forEach(key => {
      const cleanKey = _.camelCase(key.replace('permission_setting', ''))
      permissions[cleanKey] = permissions[key]
      delete permissions[key]
    })

    const isSnAdmin = this.isSnAdmin
    this.setUser({ ...this.user, guid, firstName, lastName, locked, profilePictureUrl, timezone, isSnAdmin, permissions, transactionsRefreshInterval, company, companyLocked, featureFlags })
    await this.loadViewAsCompany()
    await this.getOrgs()

    if (cookie.get('tmp_evault_staging') != null) {
      this.setIsStaging(cookie.get('tmp_evault_staging') === true || cookie.get('tmp_evault_staging') === 'true')
      cookie.remove('tmp_evault_staging')
    } else if (isSnAdmin && !this.viewAsCompany) {
      this.setIsStaging(this.getDefaultStaging())
    }

    setTimeout(() => {
      this.loading = false
      this.setAppInitialized(true)
    }, 500)

    // 'visibililtychange' listens for when browser tab becomes (in)active
    this.tabActiveListener = document.addEventListener('visibilitychange', this.handleTabVisibilityChange)
    this.windowResizeListener = window.addEventListener('resize', this.handleWindowResize)
  },

  async mounted () {
    // Wait for router to be ready and route to be set
    if (this.$route.name && this.$route.name !== 'Login') {
      const requiresOrg = snEvaultService.requiresOrg(this.$route.name)
      if (requiresOrg) {
        // if the route requires an org, find the org and select it (regardless of current mersOrgId)
        await this.getRouteOrg()
      }

      // Wait a moment to ensure all state is properly set
      await this.$nextTick()

      const canAccess = snEvaultService.canAccess(this.user, this.$route.name, !!this.mersOrgId)
      if (!canAccess) {
        this.$router.push(snEvaultService.getDefaultPage(this.user, !!this.mersOrgId))
      }
    }

    this.syncToastPopover()
  },

  beforeDestroy () {
    document.removeEventListener('visibilitychange', this.tabActiveListener)
    window.removeEventListener('resize', this.windowResizeListener)
  },

  methods: {
    ...mapMutations([
      'becomeCompany',
      'resetAppTimeout',
      'setActiveHeaderTab',
      'setAppInitialized',
      'setBreadcrumbs',
      'setHeaderTabs',
      'setHeaderText',
      'setIsStaging',
      'setOrgs',
      'setMersOrgId',
      'setOrgGuid',
      'setUser',
      'setSubtitleText',
      'setTimeoutModalVisible',
      'setProfileSettingsVisible'
    ]),

    async getUser () {
      const { data } = await this.$apollo.query({
        query: user,
        variables: {}
      })

      if (data.user == null) {
        this.setUser({ loggedIn: false, permissions: {} })
      } else {
        this.isSnAdmin = data.user.admin
        this.setUser({ ...data.user, permissions: {}, loggedIn: true })
      }
      return data.user
    },

    async getRouteOrg () {
      const { data } = await this.$apollo.query({
        query: routeOrg,
        variables: {
          route: this.$route.name,
          guid: this.$route.params.guid,
          isTest: this.isStaging
        },
        fetchPolicy: 'network-only'
      })

      if (data.routeOrg) {
        if (data.routeOrg.guid) {
          this.setOrgGuid(data.routeOrg.guid)
        }
        if (data.routeOrg.remoteId) {
          this.setMersOrgId(data.routeOrg.remoteId)
        }
        const chartContextCookie = cookie.get('tmp_evault_chart_to_report_context')
        let appliedFromChartContext = false
        if (chartContextCookie) {
          try {
            const ctx = JSON.parse(chartContextCookie)
            if (ctx.reportGuid && ctx.reportGuid === this.$route.params.guid) {
              this.setIsStaging(ctx.isTest === true || ctx.isTest === 'true')
              appliedFromChartContext = true
            }
          } catch (e) {
            // fall through to default
          }
        }
        if (!appliedFromChartContext) {
          this.setIsStaging(data.routeOrg.isTest)
        }
        if (this.user.isSnAdmin && data.routeOrg.company != null && this.$route.query.from !== 'data_dashboard') {
          this.becomeCompany(data.routeOrg.company)
          // Also set sessionStorage for admin access control
          sessionStorage.setItem('evault_view_as_company_guid', data.routeOrg.company.guid)
        }
      }
    },

    async getOrgs () {
      if (this.user.isSnAdmin && !this.companyGuid) {
        this.setOrgGuid(null)
        this.setMersOrgId(null)
        return
      }

      const { data } = await this.$apollo.query({
        query: orgs,
        variables: {
          companyGuid: this.companyGuid
        }
      })

      if (data.orgs) {
        this.setOrgs(data.orgs.nodes)
        const orgGuids = this.orgs.map(org => org.guid)
        const orgIds = this.orgs.map(org => org.remoteId)
        const cookieOrgGuid = cookie.get('evault_org_guid')
        const cookieMersOrgId = cookie.get('evault_mers_org_id') || cookie.get('tmp_evault_org_id')

        if (orgGuids.includes(cookieOrgGuid)) {
          this.setOrgGuid(cookieOrgGuid)
          this.setMersOrgId(cookieMersOrgId)
        } else if (orgGuids.length > 0) {
          this.setOrgGuid(orgGuids[0])
          this.setMersOrgId(orgIds[0])
        } else {
          this.setOrgGuid(null)
          this.setMersOrgId(null)
        }
      }
    },

    async loadViewAsCompany () {
      if (!this.isSnAdmin) {
        this.becomeCompany(null)
        return null
      }
      // Using a cookie so that when user opens a new tab they still have a way to load the company guid. For example, link in monolith.
      const viewAsCompanyGuid = sessionStorage.getItem('evault_view_as_company_guid') || cookie.get('tmp_evault_company_guid')
      cookie.remove('tmp_evault_company_guid')

      if (viewAsCompanyGuid) {
        const { data } = await this.$apollo.query({
          query: company,
          variables: {
            companyGuid: viewAsCompanyGuid
          }
        })
        this.becomeCompany(data.company)

        const mersOrgId = sessionStorage.getItem('evault_mers_org_id') || cookie.get('tmp_evault_org_id') || cookie.get('evault_mers_org_id')
        cookie.remove('tmp_evault_org_id')
        this.setMersOrgId(mersOrgId)
      }
    },

    showProfileDropdown () {
      this.resetAppTimeout()
      if (!this.profileDropdownVisible) {
        this.profileDropdownVisible = true
      }
    },

    toggleIsStaging () {
      const matchedPath = this.$route.matched[0].path
      const basePath = matchedPath.split(':')[0]
      this.setIsStaging(!this.isStaging)

      if (basePath !== matchedPath) {
        this.$router.push(basePath)
      }
    },

    getDefaultStaging () {
      return process.env.RAILS_ENV === 'development' || process.env.RAILS_ENV === 'staging'
    },

    becomeSnAdmin () {
      const applyAdminView = () => {
        this.becomeCompany(null)
        this.setIsStaging(this.getDefaultStaging())
        this.setOrgGuid(null)
        this.setMersOrgId(null)
      }
      // Vue Router 3 rejects a push to the route you are already on
      // (NavigationDuplicated), so a lone .then() never ran when this was
      // clicked from /companies - the banner stayed and the view-as state was
      // never cleared. Passing the same handler as the rejection callback
      // applies the state change either way and consumes the rejection, which
      // was otherwise unhandled.
      this.$router.push('/companies').then(applyAdminView, applyAdminView)
    },

    closeProfileDropdown () {
      this.resetAppTimeout()
      this.showNotificationDropdown = false
      this.profileDropdownVisible = false
    },

    logout () {
      sessionStorage.clear()
      window.location.assign('/logout')
    },

    handleTabSelect (event) {
      const index = event.detail?.index ?? event.detail
      if (typeof index !== 'number' || !this.headerTabs[index]) {
        return
      }
      this.toggleTabs(this.headerTabs[index].id)
    },

    toggleTabs (tabId) {
      this.activeTabIndex = this.headerTabs.map(tab => tab.id).indexOf(tabId)
      const tab = this.headerTabs[this.activeTabIndex]
      this.setActiveHeaderTab(tab.key)
      if (tab.breadcrumb != null) {
        const breadcrumbs = this.$store.getters.breadcrumbs
        if (breadcrumbs != null && breadcrumbs.length > 1) {
          breadcrumbs.pop()
        }
        breadcrumbs.push({ text: tab.breadcrumb })
        this.setBreadcrumbs(breadcrumbs)
      }
    },

    async handleTabVisibilityChange () {
      if (document.visibilityState === 'visible') {
        const authIsValid = await this.getAuthStatus()
        if (!authIsValid) {
          this.logout()
        }

        const now = new Date().getTime()
        const minutesInactive = (now - this.tabInactiveTimestamp) / 60000

        if (minutesInactive > 15) {
          this.logout()
        } else if (minutesInactive > 10) {
          this.timeoutModalTotalSeconds = Math.floor((15 - minutesInactive) * 60)
          this.setTimeoutModalVisible(true)
        } else {
          const minutesUntilModalShows = 10 - minutesInactive
          this.resetAppTimeout(minutesUntilModalShows)
        }
      } else {
        this.tabInactiveTimestamp = new Date().getTime()
      }
    },

    navItemDataCy (title) {
      return `${title.toLowerCase().replace(/\s/g, '-')}-link`
    },

    isCurrentNavItem (link) {
      return this.$route.path.startsWith(link)
    },

    navigateTo (link) {
      if (this.$route.path !== link) {
        this.$router.push(link)
      }
    },

    handleWindowResize () {
      const fullSideMenu = this.$refs.sideMenu
      // The watcher above can fire before the nav is rendered, and on routes
      // like Login it never renders at all.
      if (!fullSideMenu) {
        return
      }
      const viewingAs = this.$refs.viewingAs?.$el
      if (viewingAs) {
        // trueViewingAsHeight is the height of the viewingAs element if it's ever rendered.
        // we still get into this block if the viewingAs element is hidden, so we save the height while we have it
        // so that we can determine if we have enough room to show it again
        this.trueViewingAsHeight = viewingAs.clientHeight
      }

      const viewingAsHeight = viewingAs?.clientHeight || this.trueViewingAsHeight || 130

      // This used to estimate the space above the footer as
      // sideNavOptions.length * 65, which over-reserved badly - rows are 56px
      // and a divider entry counts as one item while rendering 1px, so eleven
      // entries reserved 715px for ~561px of content and the footer stayed
      // hidden at sizes where it fit.
      //
      // The items are summed rather than read off the last one's laid-out
      // position: the list is a flex item, and rendering the footer compresses
      // it by ~3px, so a position-based measurement answers differently
      // depending on whether the footer is currently shown. That is enough to
      // straddle the threshold and make the decision bistable - once hidden it
      // could never come back. Summing intrinsic heights is independent of the
      // state being measured.
      const list = this.$refs.sideMenuList
      let contentBottom = 0
      if (list) {
        const { paddingTop, paddingBottom } = getComputedStyle(list)
        contentBottom = list.offsetTop + parseFloat(paddingTop) + parseFloat(paddingBottom) +
          Array.from(list.children).reduce((total, item) => total + item.offsetHeight, 0)
      }

      this.showViewingAs = fullSideMenu.clientHeight - contentBottom >= viewingAsHeight
    },

    async getAuthStatus () {
      const authGuid = cookie.get('evault_auth_guid')
      const { data } = await axios.post('/auth/v1/sync', { auth_guid: authGuid })
      return data.success
    },

    handleToastAction (data) {
      switch (data.dataSource) {
        case 'uploadDocs':
          this.failedUploadDocsFileNames = data.data
          this.toggleFailedUploadDocsModal()
          break
        case 'errorOverflow':
          this.errorOverflow = data.data
          this.errorOverflowModalTitle = data.title
          this.toggleErrorOverflowModal()
          break
        case 'attachToEnote':
          this.$router.push(`/enotes/${data.data}`)
          break
        case 'detachFromEnote':
          this.$router.push(`/electronic_records/${data.data}`)
          break
        case 'editReport':
          this.$router.push(`/reports/${data.data}`)
          break
        default:
          break
      }
    },

    toggleFailedUploadDocsModal () {
      this.showFailedUploadDocsModal = !this.showFailedUploadDocsModal
    },

    toggleErrorOverflowModal () {
      this.showErrorOverflowModal = !this.showErrorOverflowModal
    },

    // Keeps the toast wrapper's top-layer (Popover API) state in sync with
    // whether there are any active toasts to show. A [popover] element stays
    // display:none until showPopover() is called, so without this the toaster
    // never renders at all. Guarded with .matches() since calling
    // showPopover()/hidePopover() while already in that state throws, and with
    // the showPopover existence check for browsers that don't support the
    // Popover API yet (CLO-13758).
    syncToastPopover () {
      const el = this.$refs.toastPopover
      if (!el || typeof el.showPopover !== 'function') {
        return
      }
      const hasActiveToasts = this.evaultToastMixin_activeToasts.length > 0
      if (hasActiveToasts && !el.matches(':popover-open')) {
        el.showPopover()
      } else if (!hasActiveToasts && el.matches(':popover-open')) {
        el.hidePopover()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui/index';

.content {
  width: 100vw;
}

.page-content {
  z-index: 100;
}

/* .evault-content is the app's scroll region. This container is pinned to the
   viewport and must not scroll as well, or the page shows two scrollbars.
   ngc-banner is not fixed-position the way sn-banner was, so the banner is a
   flex item here and .evault-container takes the remaining height instead of
   the old fixed-73px-banner / hard-coded-72px-margin pairing. */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  overscroll-behavior: none;
}

.flex {
  display: flex;
}

.evault-banner {
  flex: 0 0 auto;
}

/* sn-banner's .app-banner was a single centred row - `display: flex;
   align-items: center; justify-content: center; gap: 8px` - holding the icon,
   the message and the text buttons together. --spacing-5 is 0.5rem, the same
   8px. The icon keeps its own 0.625rem right margin from
   .gator-banner-icon inside the shadow root, so only the message and buttons
   are spaced here. */
.evault-banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-5);
}

.evault-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.evault-content {
  overflow: auto;
}

/* position/z-index only so the drawer paints above .evault-content, which
   follows it in the DOM and would otherwise cover the shadow. sn-side-menu got
   this shadow from the .sn-shadow-depth-1 class on its v-navigation-drawer. */
.side-menu {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  height: 100%;
  overflow-y: auto;
  background: var(--color-surface-primary, #fff);
  border-right: 1px solid var(--color-border-tertiary, #e5e5e5);
  box-shadow: $sn-shadow-depth-1;

  /* sn-side-menu set Open Sans explicitly on its root to override the app-wide
     $body-font-family ('Barlow', 'Roboto', 'Arial') from assets/css/snui/
     variables.scss. Gator's own .gator-list-item font-family declaration is
     malformed - `var(--font-family-body, "Open Sans") sans-serif`, missing the
     comma - so it is dropped and the label inherits Barlow instead. */
  font-family: var(--font-family-body), sans-serif;
}

/* Matches sn-side-menu's logo box - a v-img (which fills its container's
   width) under ml-4 mt-8 mr-10 mb-2, i.e. 16/32/40/8. Gator's --spacing-6 is
   0.5625rem rather than the 1.5rem the name suggests, so the previous
   shorthand plus the fixed 32px height rendered the logo well under size. */
.side-menu-logo {
  display: flex;
  align-items: center;
  padding: var(--spacing-11) var(--spacing-12) var(--spacing-5) var(--spacing-8);

  img {
    width: 100%;
    height: auto;
  }
}

/* ngc-list-item sizes its label with var(--text-size-body-1), a token Gator
   does not ship (the one it defines is --font-size-body-1), so the declaration
   is invalid at computed-value time and the label falls back to the inherited
   font size. Defining it restores sn-side-menu's 17px/24px nav text.
   Row height comes from density="comfortable" on the items - 16px of padding
   around the 24px line box is sn-side-menu's 56px .tile. Its horizontal inset
   is 16px rather than the 24px SNUI used (v-list-item's own 16px plus the px-2
   on its title); density sets the padding shorthand from inside the shadow
   root, later in the sheet than the rule that reads --ngc-list-item-padding,
   so the two cannot be combined and 24px is not reachable through it.
   Active items were a filled pill because Gator paints --color-surface-brand
   behind the selection; sn-side-menu left the row unfilled and bolded the
   label, which Gator already does. */
.side-menu-list {
  flex: 1 1 auto;
  /* sn-side-menu rendered an empty <h4 class="subheader"> between the logo and
     the list - eVault passes subheader-text="" - and its 24px bottom margin was
     load-bearing for this gap. ngc-list has no default padding of its own, so
     that space has to be restored here. */
  padding-top: var(--spacing-10);

  --text-size-body-1: var(--font-size-body-1);
  --ngc-list-item-hover-background: var(--color-surface-brand);
  --ngc-list-item-selected-background: transparent;
}

/* The icon gap reads --spacing-5 directly and has no --ngc-* hook, so the
   token itself has to be widened to sn-side-menu's 16px. It has to land on the
   list items rather than on .side-menu-list: every Gator component adopts the
   token sheets into its own shadow root under ':host', which re-declares
   --spacing-5 and so beats any inherited value. An outer-tree rule on the host
   element does win against ':host'. The comfortable density reads --spacing-8
   for its padding, so this only moves the gap. */
.side-menu-list ngc-list-item {
  --spacing-5: var(--spacing-8);
}

/* sn-header-tabs drew no top or side border on an inactive tab and only
   revealed one on hover. Gator's large tabs draw them at rest; the hover rule
   sets its own border color rather than reading this custom property, so
   clearing the resting color restores the SNUI look and keeps the hover
   affordance. */
.evault-header {
  --ngc-tabs-tab-border-color-large: transparent;
}

/* sn-breadcrumbs drew its underline as `border-bottom` on `a.v-breadcrumbs__item`,
   and Vuetify's Routable only emits an <a> when the item carries an href -
   otherwise a <div>, which never matched that selector (confirmed against prod,
   which renders `<div class="v-breadcrumbs__item">`). So under SNUI "underlined"
   meant "navigable" for free. ngc-breadcrumb instead renders every non-current
   crumb as an ngc-text-link whatever its href, and .gator-text-link hardcodes
   `text-decoration: underline`, so href-less crumbs came out looking clickable -
   companyDetails and transactionGroupDetails both pass a middle crumb with no
   href. Only the decoration *color* is themeable, so it is cleared here rather
   than the decoration itself; that is the same trick SNUI used to opt its last
   crumb out (`border-bottom: 1px solid transparent`). The hover pair is pinned
   too, because a <div> had no hover response.

   Not fully at parity: ngc-text-link's no-href fallback is
   `<div role="link" tabindex="0">` with an Enter handler, so the crumb is still
   a phantom tab stop with a pointer cursor. That is not reachable from outside
   Gator and is raised as a design-system gap. */
.evault-header ngc-breadcrumb:not([href]) {
  --ngc-text-link-underline-color: transparent;
  --ngc-text-link-underline-color--hover: transparent;
  --ngc-text-link-color--hover: var(--color-text-brand);
}

/* sn-side-menu used a bare <v-divider>, which carries no margin of its own -
   display is set only because the ngc-divider host is inline by default.
   ngc-divider defaults to --color-border-secondary (#BFBFBF), a shade darker
   than the rule above Viewing As and the side menu's own right edge, both of
   which are --color-border-tertiary (#E5E5E5). Pinned so the three agree. */
.side-menu-divider {
  display: block;

  --ngc-divider-color: var(--color-border-tertiary);
}

.side-menu-footer {
  margin-top: auto;
}

/* Vuetify's reset sets `html { box-sizing: border-box }` and `* { box-sizing:
   inherit }`, but `inherit` resolves against the flat-tree parent - for slotted
   content that is the container around <slot> inside ngc-page-header's shadow
   root, which document stylesheets never reach and so keeps the initial
   content-box. The chain has to be re-established here or everything slotted
   into the header (the profile dropdown especially) sizes as content-box and
   its fixed min-heights stack on top of their padding. */
.header-actions {
  display: flex;
  position: relative;
  align-items: center;
  gap: var(--spacing-2);
  box-sizing: border-box;
  /* sn-avatar carried mx-2 inside sn-header, so it sat 8px in from the header's
     content edge. Applied to this box rather than the avatar so the dropdown,
     which is positioned against it, moves with the avatar instead of staying
     put. */
  margin-right: var(--spacing-5);

  /* The ngc-avatar host has no display of its own and wraps an inline-flex box,
     so it sits on a text baseline and leaves ~6px of descender space below -
     which inflates this container and, with it, the dropdown's offset. */
  ngc-avatar {
    display: flex;
  }
}

// Neutralizes the browser's default [popover] styling (which otherwise centers
// it as a bordered box via inset:0/margin:auto) - sn-toaster positions itself
// internally via its own fixed-position CSS, unaffected by any of this; this
// wrapper only needs to exist to get promoted into the top layer (CLO-13758).
.toast-popover {
  inset: auto;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  overflow: visible;
  width: auto;
  height: auto;
}
</style>
